import { t, Trans } from '@lingui/macro';
import { Col, Input, List, Row } from 'antd';
import AuthActions from 'bukku/redux/Auth/actions';
import CompanyActions from 'bukku/redux/Company/actions';
import Fuse from "fuse.js";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import Cookies from 'universal-cookie';
import { CookieKey } from '../../../enums/CookieKey';
import { CompanySelectCard } from './CompanySelectCard';

const Styled = styled.div`
    --padding-30: 30px;
    --padding-60: 60px;

    padding: var(--padding-60);

    @media only screen and (width <= 576px) {
        padding: var(--padding-30);
    }
    
    .header-col {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;

        .logo-container {
            .bukku-logo {
                height: 43.55px;
            }

            margin-bottom: 1rem;
        }
    
        .title-container {
            font-size: 24px;
            font-weight: 500;
            text-align: center;
        }
    
        .subtitle-container {}
    }

    .searchbar-col {
        display: flex;
        justify-content: center;

        .searchbar {
            width: 30ch;
        }
    }

    .card-list-col {
        height: calc(100vh - 120px - 32px - 32px - var(--padding-60) - var(--padding-60));
        padding: 0 4px;
        overflow-y: scroll;

        @media only screen and (width <= 576px) {
            height: calc(100vh - 120px - 32px - 32px - var(--padding-30) - var(--padding-30));
        }

        @media only screen and (width <= 430px) {
            height: calc(100vh - 158px - 32px - 32px - var(--padding-30) - var(--padding-30));
        }
    }

`;

const {
    logout,
} = AuthActions;
const { readCompanies } = CompanyActions;

export function CompanySelectPage() {
    const fuseRef = useRef(null);
    const [parsedCompanies, setParsedCompanies] = useState([]);
    const dispatch = useDispatch();
    const onClickLogout = () => {
        dispatch(logout());
    };

    const getRecentlyLoggedIn = () => {
        const cookies = new Cookies();
        const subdomain = cookies.get(CookieKey.last_logged_in_cs);
        const email = cookies.get(CookieKey.last_logged_in_email);
        return {subdomain, email};
    };

    const companies = useSelector(state => state.Company.companies, shallowEqual);

    const getReorderedCompanies = useCallback(companyArray => {
        const {subdomain: recentLoginSubdomain} = getRecentlyLoggedIn();

        let reorderedCompanies = [...companyArray];

        // reorder such that recent logged in is placed on top
        if (recentLoginSubdomain) {
            const recentLoginCompany = companyArray.find(c => c.subdomain === recentLoginSubdomain);
            const otherCompanies = reorderedCompanies
                .filter(c => c.subdomain !== recentLoginSubdomain);
            reorderedCompanies = recentLoginCompany ?
                [recentLoginCompany, ...otherCompanies] :
                reorderedCompanies;
        }

        return reorderedCompanies;
    }, []);

    useEffect(() => {
        dispatch(readCompanies());
        return () => {};
    }, [dispatch]);

    // on companies loaded
    useEffect(() => {
        const reorderedCompanies = getReorderedCompanies(companies);
        setParsedCompanies(reorderedCompanies);

        const fuseOptions = {
            threshold: 0.5,
            includeMatches: true,
            keys: [
                { name: "legal_name", weight: 1},
                { name: "subdomain", weight: 1 },
            ],
        };
        const fuse = new Fuse(reorderedCompanies, fuseOptions);
        fuseRef.current = fuse;

        return () => {
            setParsedCompanies([]);
        };
    }, [companies, getReorderedCompanies]);

    const onSearch = value => {
        if (!value) {
            setParsedCompanies(getReorderedCompanies(companies));
        }
        else {
            const fuseSearchResults = fuseRef.current.search(value).map(r => r.item);
            setParsedCompanies(getReorderedCompanies(fuseSearchResults));
        }
    };

    const {
        subdomain: recentSubdomain,
        email: recentEmail,
    } = getRecentlyLoggedIn();

    return (
        <Styled id="wrapper">
            <Row gutter={[12, 16]}>
                <Col span={24} className="header-col">
                    <div className="logo-container">
                        <img
                            className="bukku-logo"
                            src="/assets/img/logo/logo-landscape-light.png"
                            alt="logo"
                        />
                    </div>
                    <div className="title-container">
                        <Trans>Please Access via Your Bukku URL</Trans>
                    </div>
                    <div className="subtitle-container">
                        <Trans>
                            Signed in to&nbsp;
                            <strong>{recentEmail}</strong>
                            .
                        </Trans>
                        &nbsp;
                        <button className="link" onClick={onClickLogout} type="button">
                            <Trans>Logout</Trans>
                        </button>
                    </div>
                </Col>

                {/* <Col span={24}>
                    <div className="featured-company-container">
                        <CompanySelectCard />
                    </div>
                </Col> */}

                {
                    companies.length > 1 ?
                        <Col span={24} className="searchbar-col">
                            <Input.Search
                                allowClear
                                className="searchbar"
                                placeholder={t`Search ${companies.length} companies`}
                                onSearch={onSearch}
                            />
                        </Col> : null
                }

                <Col span={24} className="card-list-col">

                    <List
                        grid={{
                            gutter: 8,
                            xs: 1,
                            sm: 2,
                            md: 3,
                            lg: 3,
                            xl: 4,
                            xxl: 4,
                        }}
                        dataSource={parsedCompanies}
                        renderItem={item => (
                            <List.Item>
                                <CompanySelectCard
                                    company={item}
                                    isFeatured={item.subdomain === recentSubdomain}
                                />
                            </List.Item>
                        )}
                    />
                </Col>

            </Row>

        </Styled>
    );
}
